import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useState } from 'react';
import { EditButton, ShowContextProvider, ShowView, TabbedShowLayout, Tab, TextField, DateField, BooleanField, ChipField, NumberField, TopToolbar, useShowController, useRecordContext, SimpleShowLayout } from 'react-admin';
import ContextTitle from '../../Components/ContextTitle';
import { ALLOWED_CONFIG_EDITORS } from './ConfigEditor/ConfigEditor';
import { getIntegrationConfig } from '../../types/integrations';
import FileHistoryTab from './FileHistoryTab';
import CacheTab from './CacheTab';

const RawConfigField = () => {
  const record = useRecordContext();
  const [copied, setCopied] = useState(false);
  const integrationConfig = getIntegrationConfig(record);

  if (integrationConfig == null) {
    return <Box sx={{ p: 2, color: 'text.secondary', fontStyle: 'italic' }}>No configuration available</Box>;
  }

  const json = JSON.stringify(integrationConfig, null, 2);

  const handleCopy = () => {
    navigator.clipboard.writeText(json).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
        <IconButton size="small" onClick={handleCopy} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box
        component="pre"
        sx={{
          p: 2,
          pr: 5,
          m: 0,
          backgroundColor: 'action.hover',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 600,
          fontSize: '0.85rem',
          fontFamily: 'monospace'
        }}
      >
        {json}
      </Box>
    </Box>
  );
};

const HcmShow = () => {
  const controllerProps = useShowController();
  const userEmail = JSON.parse(localStorage.getItem('GoogleAccountInfo') || '{}').email || '';
  const canEdit = ALLOWED_CONFIG_EDITORS.includes(userEmail);

  const actions = canEdit ? (
    <TopToolbar>
      <EditButton />
    </TopToolbar>
  ) : (
    false
  );

  return (
    <ShowContextProvider value={controllerProps}>
      <ShowView title={<ContextTitle context="uuid" />} actions={actions}>
        <TabbedShowLayout>
          <Tab label="General">
            <TextField source="uuid" label="UUID" />
            <TextField source="baseUrl" label="Base URL" />
            <TextField source="vendor" label="Vendor" />
            <TextField source="source" label="Source" />
            <TextField source="companyUuid" label="Company UUID" />
            <TextField source="companyName" label="Company Name" />
            <ChipField source="status" label="Status" />
            <NumberField source="syncInterval" label="Sync Interval (seconds)" />
            <DateField source="createdAt" label="Created At" showTime />
            <DateField source="updatedAt" label="Updated At" showTime />
          </Tab>
          <Tab label="Sync Status">
            <BooleanField source="lastSyncSuccess" label="Last Sync Success" />
            <DateField source="lastSyncStartedAt" label="Last Sync Started At" showTime />
            <DateField source="lastSyncSuccessDate" label="Last Sync Success Date" showTime />
          </Tab>
          <Tab label="OT Credentials">
            <SimpleShowLayout>
              <TextField source="otCredentials.clientId" label="Client ID" />
              <TextField source="otCredentials.clientSecret" label="Client Secret" />
            </SimpleShowLayout>
          </Tab>
          <Tab label="Configuration">
            <RawConfigField />
          </Tab>
          <Tab label="File History">
            <FileHistoryTab />
          </Tab>
          <Tab label="Cache">
            <CacheTab />
          </Tab>
        </TabbedShowLayout>
      </ShowView>
    </ShowContextProvider>
  );
};

export default HcmShow;
